import React, { useState } from 'react';
import AsyncSelect from 'react-select/async';

import SidebarItem from './SidebarItem';

import {
  IconArrowLeft,
  IconArrowRight,
  IconCursor,
  IconHelpCircle,
  IconShareExternalLink,
  IconUser,
  IconWhatsapp,
} from '@hubii/icons';
import Button from '../Button/Button';

import { useIsMobile } from '../../hooks/useIsMobile';

import { generateTestId } from './data';
import Popover from '../../components/Popover';
import { useNavigate } from 'react-router-dom';

interface NavatticEmbed {
  openPopup: (_url: string) => void;
}

declare global {
  interface Window {
    NavatticEmbed?: NavatticEmbed;
  }
}

export interface SidebarProps {
  dataTestId: string;
  navItems: {
    label: string;
    url: string;
    icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    isActive?: boolean;
  }[];
  logout?: () => void;
  selectedCompany?: IOptions;
  onChangeCompany?: (_hub: IOptions) => void;
  companiesOptions?: IOptions[];
  userCurrentHub?: string | null;
  showHelp?: boolean;
}

interface IOptions {
  value: string;
  label: string;
}

const HELP_CENTER_URL = 'https://crm.hubii.co/hc/ajuda/pt_BR/';
const WHATSAPP_SUPPORT_URL = 'https://wa.me/554198739502';
const HELP_TUTORIAL_URL = '/pedidos/tutorial';

const Sidebar = (props: SidebarProps) => {
  const { dataTestId, navItems, logout, selectedCompany, onChangeCompany, companiesOptions, userCurrentHub, showHelp } =
    props;
  const { isMobile } = useIsMobile();
  const testIds = generateTestId(dataTestId);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const navigate = useNavigate();

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const filterHubs = (inputValue: string) => {
    return companiesOptions?.filter((i: IOptions) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
  };

  const loadOptions = (searchValue: string): Promise<IOptions[]> => {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(filterHubs(searchValue) || []);
      }, 1000);
    });
  };

  return (
    <div data-testid={testIds.component} className={`flex h-screen bg-white `}>
      <div
        className={` z-30 inset-y-0 left-0  bg-white text-white transform border-r border-gray-100 p-3 flex flex-col gap-3 ${
          isSidebarOpen || isMobile ? 'w-72' : 'w-auto items-center'
        }  transition-all duration-300 ease-in-out`}
      >
        <div className='flex items-center justify-between'>
          <div
            className={`flex items-center gap-4 w-full ${isSidebarOpen || isMobile ? 'flex-row  justify-between' : 'flex-col'}`}
          >
            <svg width='27' height='30' viewBox='0 0 27 30' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M10.1115 15.8611C10.658 18.6287 10.2943 21.3548 9.09441 23.6413C8.90918 23.9957 9.01642 24.4289 9.35545 24.6522L11.4121 26.0063C11.7981 26.2602 12.3311 26.1398 12.5519 25.7396C14.2383 22.7006 14.8561 18.9964 14.0889 15.1064C13.801 13.6516 13.3351 12.2753 12.7081 11.0064C12.4941 10.5711 11.9274 10.4376 11.5281 10.7198L9.4983 12.153C9.1927 12.3696 9.08347 12.7634 9.23726 13.1026C9.62086 13.9558 9.91771 14.8791 10.1139 15.8658L10.1117 15.8615V15.8611H10.1115Z'
                fill='#2D254D'
              />
              <path
                d='M8.69283 5.93444C8.33371 5.64346 7.79612 5.7266 7.53289 6.10519L6.13658 8.1225C5.90678 8.45504 5.97383 8.89921 6.286 9.15954C6.6318 9.45052 6.95949 9.76569 7.26966 10.1025C7.54403 10.4001 7.99906 10.4548 8.33132 10.2206L10.3456 8.80066C10.7226 8.53583 10.7941 8.00638 10.4906 7.66291C9.94191 7.03725 9.34204 6.45959 8.69283 5.93444Z'
                fill='#746BFF'
              />
              <path
                d='M12.6743 0.166974C12.3106 -0.113071 11.782 -0.0320827 11.5211 0.344173L10.136 2.34391C9.90399 2.68094 9.97323 3.1337 10.2944 3.38759C11.2758 4.16216 12.1747 5.04815 12.9777 6.03056C13.2432 6.35432 13.7183 6.41774 14.0619 6.17712L16.0582 4.76811C16.4263 4.50777 16.4999 3.99803 16.2188 3.64793C15.1749 2.35269 13.9883 1.18431 12.6721 0.166974H12.6743Z'
                fill='#746BFF'
              />
              <path
                d='M15.2374 8.09813C14.9185 8.32353 14.8136 8.74351 14.9943 9.08698C15.8619 10.7366 16.5133 12.5591 16.9037 14.5306C17.8338 19.2367 17.0822 23.7787 14.9788 27.4915C14.778 27.8459 14.8851 28.2922 15.2285 28.5198L17.2762 29.8675C17.6619 30.1212 18.1862 29.9987 18.416 29.6048C21.1127 24.9621 21.9893 19.3809 20.8809 13.7755C20.3969 11.325 19.5626 9.0257 18.4228 6.94068C18.1952 6.525 17.6442 6.40244 17.2561 6.678L15.2418 8.10008L15.2374 8.09794V8.09813Z'
                fill='#2D254D'
              />
            </svg>

            {(isSidebarOpen || isMobile) && companiesOptions && (
              <AsyncSelect
                cacheOptions
                loadOptions={loadOptions}
                defaultOptions
                onChange={e => {
                  onChangeCompany && onChangeCompany(e as IOptions);
                  setIsSidebarOpen(false);
                }}
                value={selectedCompany}
                data-testid='select-hub'
                placeholder='Selecione um hub...'
                className='w-full'
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? '#5A54FF' : '#c5c5c5',
                    color: '#6f6f6f',
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: state.isSelected ? '#EEEEFF' : 'white',
                    color: state.isSelected ? '#5A54FF' : '#6f6f6f',
                    ':hover': {
                      backgroundColor: '#EEEEFF',
                      color: '#5A54FF',
                    },
                  }),
                }}
              />
            )}

            {!isMobile && (
              <Button
                onClick={toggleSidebar}
                dataTestId='button-sidebar'
                variant='outlined'
                color='neutral'
                size='small'
                className={isSidebarOpen ? '' : ''}
              >
                {isSidebarOpen ? <IconArrowLeft className='w-4 h-4' /> : <IconArrowRight className='w-4 h-4' />}
              </Button>
            )}
          </div>
        </div>
        <div className='mt-5 flex flex-col justify-between h-full'>
          <div className='flex flex-col'>
            {navItems.map(item => (
              <SidebarItem
                key={item.label}
                dataTestId={`${dataTestId}-${item.label}`}
                Icon={item.icon}
                label={item.label}
                url={item.url}
                isSidebarOpen={isSidebarOpen || isMobile}
                isActive={item.isActive}
              />
            ))}
          </div>

          <div className='flex flex-col'>
            {showHelp && (
              <div className='flex flex-col gap-2'>
                <Button
                  variant={isMobile ? 'outlined' : 'icon'}
                  color='neutral'
                  size='small'
                  className='w-full'
                  dataTestId='button-help'
                  onClick={e => setAnchorEl(e.currentTarget)}
                  align={isMobile ? 'left' : 'center'}
                >
                  <IconHelpCircle className='text-gray-500 hover:text-gray-700' />
                  {isMobile && 'Ajuda'}
                </Button>

                <div className='relative'>
                  <Popover
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    offsetY={10}
                    placement='top'
                    sameWidth={false}
                  >
                    <div className='shadow-sm bg-white border border-gray-100 rounded-md p-2 gap-1 flex flex-col z-10 w-auto'>
                      <Button
                        color='neutral'
                        variant='text'
                        onClick={() => {
                          window.open(HELP_CENTER_URL, '_blank');
                          setAnchorEl(null);
                        }}
                        dataTestId='button-help-center'
                        align='left'
                        size='small'
                      >
                        <IconShareExternalLink className='h-5 w-5' />
                        Ir para a central de ajuda
                      </Button>

                      <Button
                        color='neutral'
                        variant='text'
                        onClick={() => {
                          setAnchorEl(null);
                          navigate(HELP_TUTORIAL_URL);
                        }}
                        dataTestId='button-help-tutorial'
                        align='left'
                        size='small'
                      >
                        <IconCursor className='h-5 w-5' />
                        Acessar tutorial de pedido
                      </Button>

                      <Button
                        color='neutral'
                        variant='text'
                        onClick={() => {
                          setAnchorEl(null);
                          window.open(WHATSAPP_SUPPORT_URL, '_blank');
                        }}
                        dataTestId='button-help-support'
                        align='left'
                        size='small'
                      >
                        <IconWhatsapp className='h-5 w-5' />
                        Fale com o suporte
                      </Button>
                    </div>
                  </Popover>
                </div>
              </div>
            )}

            <SidebarItem
              hasBorder
              dataTestId={`${dataTestId}-logout`}
              Icon={IconUser}
              label={userCurrentHub ? `${userCurrentHub} - Sair` : 'Sair'}
              url='#'
              action={logout}
              isSidebarOpen={isSidebarOpen || isMobile}
              isActive={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
